// components/Alerts.jsx
import React, { useState, useEffect } from "react";
import { TriangleAlert, X, CheckCircle, Ban } from "lucide-react";
import constant from "../../../constant";

const Alerts = ({ hospitalId, patientId, trends = false }) => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalNote, setModalNote] = useState("");   // ← New state

    const formatTime = (dateString) => {
        const d = new Date(dateString);
        const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
        const day = d.getUTCDate();
        const year = d.getUTCFullYear();
        let hours = d.getUTCHours();
        const minutes = String(d.getUTCMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`;
    };

    const fetchAlerts = async () => {
        const token = localStorage.getItem("token");
        if (!token || !hospitalId) {
            setLoading(false);
            return;
        }

        try {
            let url;
            if (patientId) {
                url = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/${patientId}/vital-alerts${trends ? "?trends=true" : ""}`;
            } else {
                url = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/vital-alerts`;
            }

            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.ok) {
                const data = await res.json();
                const alertsArray = Array.isArray(data) ? data : (data.vital_alerts || []);

                const activeAlerts = alertsArray.filter(
                    (alert) =>
                        alert.alert_status === "No Action" ||
                        alert.alert_status === "Viewed"
                );

                setAlerts((prevAlerts) => {
                    const prevIds = new Set(prevAlerts.map((a) => a.alert_id));
                    return activeAlerts.map((alert) => ({
                        ...alert,
                        isNew: !prevIds.has(alert.alert_id),
                    }));
                });
            }
        } catch (err) {
            console.error("Failed to fetch alerts:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();
        const interval = setInterval(fetchAlerts, 30000);
        return () => clearInterval(interval);
    }, [hospitalId, patientId, trends]);

    // Clear "isNew" flag
    useEffect(() => {
        const hasNew = alerts.some((a) => a.isNew);
        if (hasNew) {
            const timer = setTimeout(() => {
                setAlerts((prev) =>
                    prev.map((a) => ({ ...a, isNew: false }))
                );
            }, 7000);
            return () => clearTimeout(timer);
        }
    }, [alerts]);

    // Updated to accept and send optional note
    const updateStatus = async (alertId, patientIdInAlert, newStatus) => {
        const token = localStorage.getItem("token");

        try {
            const payload = { alert_status: newStatus };
            if (modalNote.trim()) {
                payload.alert_remarks = modalNote.trim();
            }

            const res = await fetch(
                `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/${patientIdInAlert}/vital-alerts/${alertId}`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (res.ok) {
                setAlerts((prev) => prev.filter((a) => a.alert_id !== alertId));
                setIsModalOpen(false);
                setSelectedAlert(null);
                setModalNote("");           // Reset note
            } else {
                alert("Failed to update alert status");
            }
        } catch (err) {
            alert("Network error while updating alert");
        }
    };

    const openModal = (alert) => {
        setSelectedAlert(alert);
        setModalNote("");                    // Reset note when opening
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedAlert(null);
        setModalNote("");
    };

    if (loading) {
        return (
            <div className="shadow-lg rounded-lg h-full flex items-center justify-center bg-white">
                <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-red-600"></div>
            </div>
        );
    }

    const activeCount = alerts.length;

    return (
        <>
            {/* Blinking Animation Styles */}
            <style jsx>{`
                @keyframes blink {
                    0%, 100% { background-color: rgba(239, 68, 68, 0.15); border-color: #fca5a5; box-shadow: 0 0 8px rgba(239, 68, 68, 0.3); }
                    50% { background-color: rgba(239, 68, 68, 0.35); border-color: #ef4444; box-shadow: 0 0 16px rgba(239, 68, 68, 0.6); }
                }
                .animate-blink {
                    animation: blink 1s ease-in-out 6;
                }
            `}</style>

            <div className="shadow-lg rounded-lg flex flex-col bg-white max-h-screen overflow-y-scroll">
                {/* Header */}
                <div className="bg-red-600 p-4 flex items-center justify-between">
                    <h2 className="flex items-center text-xl font-bold text-white uppercase">
                        <TriangleAlert className="mr-3" size={26} />
                        Alerts
                    </h2>
                    {activeCount > 0 && (
                        <span className="bg-white text-red-600 px-3 py-1.5 rounded-full text-sm font-bold">
                            {activeCount}
                        </span>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {alerts.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500 font-medium">No active alerts</p>
                            <p className="text-sm text-gray-400 mt-1">
                                {patientId ? "This patient is stable" : "All patients stable"}
                            </p>
                        </div>
                    ) : (
                        alerts.map((alert) => (
                            <div
                                key={alert.alert_id}
                                onClick={() => openModal(alert)}
                                className={`bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-red-300 cursor-pointer transition-all duration-200 ${alert.isNew ? "animate-blink" : ""}`}
                            >
                                <div className={`px-4 py-1 text-[0.6rem] font-bold uppercase text-center ${alert.alert_type === "red" ? "bg-red-600 text-white" : "bg-yellow-500 text-white"}`}>
                                    {alert.alert_type === "red" ? "Critical Alert" : "Warning Alert"}
                                </div>

                                <div className="px-4 py-3">
                                    <p className="text-sm font-normal text-gray-600">
                                        {formatTime(alert.alert_time)}
                                    </p>
                                    <p className="text-sm font-medium text-gray-800">
                                        {alert.alert_note}
                                    </p>
                                </div>

                                <div className="px-4 pb-3">
                                    <p className="text-xs text-gray-500 capitalize">
                                        {alert.bed_number && `Bed ${alert.bed_number}`}
                                        {alert.bed_number && alert.patient_name && " • "}
                                        {alert.patient_name || "Unknown Patient"}
                                    </p>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && selectedAlert && (
                <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-in zoom-in-95 duration-200">

                        <div className={` px-6 py-4 ${selectedAlert.alert_type === "red" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                            <div className="flex justify-between items-center">
                                <div>
                                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[0.65rem] font-bold uppercase tracking-wide text-white mb-1.5 ${selectedAlert.alert_type === "red" ? "bg-red-600" : "bg-amber-500"}`}>
                                        {selectedAlert.alert_type === "red" ? "Critical Alert" : "Warning Alert"}
                                    </span>
                                    <h3 className={`text-2xl font-bold ${selectedAlert.alert_type === "red" ? "text-red-900" : "text-amber-900"}`}>Review Alert</h3>
                                    <p className={`text-sm mt-1 ${selectedAlert.alert_type === "red" ? "text-red-600" : "text-amber-700"}`}>
                                        {formatTime(selectedAlert.alert_time)}
                                    </p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className={`rounded-lg p-2 transition-all ${selectedAlert.alert_type === "red" ? "text-red-400 hover:text-red-700 hover:bg-red-100" : "text-amber-500 hover:text-amber-800 hover:bg-amber-100"}`}
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>


                        {/* Name Row */}
                        <div className="flex px-6 py-3 gap-4 items-center border-b border-gray-100">
                            <label className="text-sm font-medium text-gray-700 w-28 flex-shrink-0">Name</label>
                            <div className="flex-1 text-lg font-semibold text-gray-900 text-right">
                            {selectedAlert.patient_name || "NA"}
                            </div>
                        </div>

                        {/* Alert Details Row */}
                        <div className="flex px-6 py-3 gap-4 items-center border-b border-gray-100">
                            <label className="text-sm font-medium text-gray-700 w-28 flex-shrink-0">Alert Type</label>
                            <div className="flex-1 text-base text-gray-700 text-right whitespace-pre-wrap">
                            {selectedAlert.alert_note || "NA"}
                            </div>
                        </div>

                        {/* Alert Note Row */}
                        <div className="flex px-6 py-3 gap-4 items-center border-b border-gray-100">
                            <label className="text-sm font-medium text-gray-700 w-28 flex-shrink-0">Additional Notes <span className="text-gray-400 font-normal">(optional)</span></label>


                            <textarea
                                value={modalNote}
                                onChange={(e) => setModalNote(e.target.value)}
                                className="w-full h-28 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                                placeholder="Add comments, actions taken, or observations..."
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={() => updateStatus(selectedAlert.alert_id, selectedAlert.patient_id, "Cancelled")}
                                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-all duration-150 flex items-center justify-center gap-2 shadow-sm"
                            >
                                <Ban size={18} />
                                Cancel Alert
                            </button>

                            <button
                                onClick={() => updateStatus(selectedAlert.alert_id, selectedAlert.patient_id, "Viewed")}
                                className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-150"
                            >
                                Mark as Viewed
                            </button>

                            <button
                                onClick={() => updateStatus(selectedAlert.alert_id, selectedAlert.patient_id, "Acknowledged")}
                                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 transition-all duration-150 flex items-center justify-center gap-2 shadow-sm"
                            >
                                <CheckCircle size={18} />
                                Acknowledge
                            </button>
                        </div>
                    </div>
                </div>
            )}




            {/* {isModalOpen && selectedAlert && (
                <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <p className="text-sm font-normal text-gray-600">
                                    {formatTime(selectedAlert.alert_time)}
                                </p>
                                <p className="text-sm font-bold mt-2 text-gray-600">
                                    {selectedAlert.patient_name}
                                </p>
                                <h3 className="text-2xl font-bold text-gray-900">Review Alert</h3>
                                <p className="text-sm font-normal text-gray-500 mt-1">
                                    {selectedAlert.alert_note}
                                </p>
                            </div>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Additional Notes <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <textarea
                                value={modalNote}
                                onChange={(e) => setModalNote(e.target.value)}
                                className="w-full h-28 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-y min-h-[100px]"
                                placeholder="Add comments, actions taken, or observations..."
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={() => updateStatus(selectedAlert.alert_id, selectedAlert.patient_id, "Cancelled")}
                                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                            >
                                <Ban size={18} />
                                Cancel Alert
                            </button>

                            <button
                                onClick={() => updateStatus(selectedAlert.alert_id, selectedAlert.patient_id, "Viewed")}
                                className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition"
                            >
                                Mark as Viewed
                            </button>

                            <button
                                onClick={() => updateStatus(selectedAlert.alert_id, selectedAlert.patient_id, "Acknowledged")}
                                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                            >
                                <CheckCircle size={18} />
                                Acknowledge
                            </button>
                        </div>
                    </div>
                </div>
            )} */}
        </>
    );
};

export default Alerts;